import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { hasCapability, type Capability, type SessionResponse, type TenantRole } from '@imsenta/shared';
import { api, ApiError } from './api';

interface SessionContextValue {
  session: SessionResponse | null;
  loading: boolean;
  /** The server could not be reached — distinct from being signed out. */
  unreachable: boolean;
  retry: () => void;
  role: TenantRole | null;
  can: (capability: Capability) => boolean;
  refresh: () => Promise<void>;
  login: (input: { email: string; password: string }) => Promise<SessionResponse>;
  register: (input: { organisationName: string; name: string; email: string; password: string }) => Promise<SessionResponse>;
  logout: () => Promise<void>;
  switchTenant: (tenantId: string) => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      try {
        return await api.get<SessionResponse>('/auth/session');
      } catch (err) {
        // 401 means "signed out". Anything else — a network drop, a 5xx — is a
        // failure to reach the server, and must not be mistaken for signing the
        // user out, which would throw away where they were.
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    retry: (failureCount) => failureCount < 2,
    staleTime: 30_000,
  });

  const session = data ?? null;
  const unreachable = Boolean(error);

  const setSession = useCallback(
    (next: SessionResponse | null) => {
      queryClient.setQueryData(['session'], next);
      // Everything else is tenant-scoped, so drop it when the session changes.
      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== 'session' });
    },
    [queryClient],
  );

  const loginMutation = useMutation({
    mutationFn: (input: { email: string; password: string }) => api.post<SessionResponse>('/auth/login', input),
    onSuccess: setSession,
  });

  const registerMutation = useMutation({
    mutationFn: (input: { organisationName: string; name: string; email: string; password: string }) =>
      api.post<SessionResponse>('/auth/register', input),
    onSuccess: setSession,
  });

  const value = useMemo<SessionContextValue>(
    () => ({
      session,
      loading: isLoading,
      unreachable,
      retry: () => void refetch(),
      role: session?.tenant?.role ?? null,
      can: (capability) => hasCapability(session?.tenant?.role ?? null, capability),
      refresh: async () => {
        await queryClient.invalidateQueries({ queryKey: ['session'] });
      },
      login: (input) => loginMutation.mutateAsync(input),
      register: (input) => registerMutation.mutateAsync(input),
      logout: async () => {
        await api.post('/auth/logout');
        setSession(null);
      },
      switchTenant: async (tenantId) => {
        const next = await api.post<SessionResponse>('/auth/switch-tenant', { tenantId });
        setSession(next);
      },
    }),
    [session, isLoading, unreachable, refetch, queryClient, loginMutation, registerMutation, setSession],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession must be used inside a SessionProvider');
  return value;
}

/** Convenience hook for capability-gated UI. */
export function useCan(capability: Capability): boolean {
  return useSession().can(capability);
}
